import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChatInbox } from '../../contexts/ChatInboxContext';
import { useCoinToast } from '../../contexts/CoinToastContext';
import { useErrorReport } from '../../contexts/ErrorReportContext';
import {
  respondToFriendRequest,
  subscribeIncomingFriendRequests,
  syncAcceptedFriends,
} from '../../services/userProfile';
import {
  markUserNotificationRead,
  subscribeUnreadUserNotifications,
} from '../../services/userNotifications';
import { chatBellItems } from '../../pages/friends/chat/chatInboxState';
import { markChatRead } from '../../services/chat/chatApi';
import { capNotificationHistory } from './notificationSeen';

/**
 * Header notifications: pending friend requests + unread userNotifications
 * (memory invites and accepted friend requests).
 */
export default function useHeaderNotifications() {
  const { currentUser, userProfile } = useAuth();
  const { reportError } = useErrorReport();
  const { notifyWalkerEarn } = useCoinToast();
  const { unreadChats, partners } = useChatInbox();
  const [friendIncoming, setFriendIncoming] = useState([]);
  const [userNotifs, setUserNotifs] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(Boolean(currentUser?.uid));
  const [memoryLoading, setMemoryLoading] = useState(Boolean(currentUser?.uid));
  const [busyId, setBusyId] = useState(null);
  const syncedAcceptedRef = useRef(new Set());

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) {
      setFriendIncoming([]);
      setFriendsLoading(false);
      return undefined;
    }
    setFriendsLoading(true);
    return subscribeIncomingFriendRequests(
      uid,
      (rows) => {
        setFriendIncoming(
          (rows || []).map((req) => ({
            ...req,
            kind: 'friend_request',
          }))
        );
        setFriendsLoading(false);
      },
      (err) => {
        console.error('incoming friend requests subscribe failed:', err);
        setFriendsLoading(false);
        reportError(err, {
          pageLabel: 'Header',
          source: 'notif-friend-subscribe',
          userMessage: 'Could not load notifications.',
        });
      }
    );
  }, [currentUser?.uid, reportError]);

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) {
      setUserNotifs([]);
      setMemoryLoading(false);
      syncedAcceptedRef.current = new Set();
      return undefined;
    }
    setMemoryLoading(true);
    return subscribeUnreadUserNotifications(
      uid,
      (rows) => {
        setUserNotifs(rows || []);
        setMemoryLoading(false);
      },
      (err) => {
        console.error('user notifications subscribe failed:', err);
        setMemoryLoading(false);
        reportError(err, {
          pageLabel: 'Header',
          source: 'notif-memory-subscribe',
          userMessage: 'Could not load notifications.',
        });
      }
    );
  }, [currentUser?.uid, reportError]);

  useEffect(() => {
    const uid = currentUser?.uid;
    if (!uid) return undefined;
    const accepted = (userNotifs || []).filter((n) => n.kind === 'friend_accepted');
    const fresh = accepted.filter((n) => !syncedAcceptedRef.current.has(n.id));
    if (!fresh.length) return undefined;
    fresh.forEach((n) => syncedAcceptedRef.current.add(n.id));
    let cancelled = false;
    syncAcceptedFriends(uid)
      .then((result) => {
        if (cancelled) return;
        if (result?.earned) notifyWalkerEarn(result.earned, userProfile);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('sync accepted friends from notif failed:', err);
        reportError(err, {
          pageLabel: 'Header',
          source: 'friend-accepted-sync',
          userMessage: 'Could not update friends.',
          silent: true,
          kind: 'intent',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, userNotifs, userProfile, notifyWalkerEarn, reportError]);

  const items = useMemo(() => {
    const chatItems = chatBellItems(unreadChats, partners, currentUser?.uid);
    const merged = [...userNotifs, ...friendIncoming, ...chatItems];
    merged.sort((a, b) => {
      const at = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
      const bt = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
      return bt - at;
    });
    return capNotificationHistory(merged);
  }, [friendIncoming, userNotifs, unreadChats, partners, currentUser?.uid]);

  const respondFriend = useCallback(
    async (requestId, accept) => {
      if (!currentUser?.uid || !requestId || busyId) return;
      setBusyId(requestId);
      try {
        const result = await respondToFriendRequest(requestId, accept, currentUser.uid);
        if (accept && result?.earned) notifyWalkerEarn(result.earned, userProfile);
      } catch (err) {
        const userMessage = err.message || 'Could not update request.';
        reportError(err, {
          pageLabel: 'Header',
          source: 'notif-friend-respond',
          userMessage,
        });
        throw err;
      } finally {
        setBusyId(null);
      }
    },
    [busyId, currentUser?.uid, userProfile, notifyWalkerEarn, reportError]
  );

  const dismissMemory = useCallback(
    async (item) => {
      if (!currentUser?.uid || !item?.id || busyId) return;
      setBusyId(item.id);
      try {
        await markUserNotificationRead(item.id, currentUser.uid);
      } catch (err) {
        reportError(err, {
          pageLabel: 'Header',
          source: 'notif-memory-dismiss',
          userMessage: err.message || 'Could not dismiss notification.',
        });
        throw err;
      } finally {
        setBusyId(null);
      }
    },
    [busyId, currentUser?.uid, reportError]
  );

  const openMemory = useCallback(
    async (item) => {
      if (!item?.id || !currentUser?.uid) return;
      try {
        await markUserNotificationRead(item.id, currentUser.uid);
      } catch (err) {
        console.warn('mark memory notif read failed:', err);
      }
    },
    [currentUser?.uid]
  );

  const openChat = useCallback(
    async (item) => {
      if (!currentUser?.uid || !item?.chatId) return;
      try {
        await markChatRead(item.chatId, currentUser.uid);
      } catch (err) {
        console.warn('mark chat read failed:', err);
      }
    },
    [currentUser?.uid]
  );

  const dismissChat = useCallback(
    async (item) => {
      if (!currentUser?.uid || !item?.chatId || busyId) return;
      setBusyId(item.id);
      try {
        await markChatRead(item.chatId, currentUser.uid);
      } catch (err) {
        reportError(err, {
          pageLabel: 'Header',
          source: 'notif-chat-dismiss',
          userMessage: err.message || 'Could not mark chat read.',
        });
        throw err;
      } finally {
        setBusyId(null);
      }
    },
    [busyId, currentUser?.uid, reportError]
  );

  return {
    items,
    loading: friendsLoading || memoryLoading,
    respondFriend,
    dismissMemory,
    openMemory,
    openChat,
    dismissChat,
    busyId,
  };
}
